import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import { resolveInitialCity, setStoredCity } from '@/utils/geo';
import { propertyPath } from '@/utils/slug';
import LocationSearch from '@/components/LocationSearch';
import { AreaBadge } from '@/components/AreaDisplay';
import { 
  AREA_UNIT_OPTIONS, 
  toSqft, 
  fromSqft,
  getDefaultUnit,
  formatArea 
} from '@/utils/areaConverter';
import { 
  Building2, Bed, MapPin, Heart, Search, Home, Trees, 
  Users, CheckCircle, Star, Shield, ArrowRight, Filter,
  Maximize, DoorOpen, Car, Ruler, AlertTriangle, BadgeCheck
} from 'lucide-react';

// Property subtypes based on main type
const propertySubtypes = {
  residential: [
    { value: 'apartment', label: 'Apartment', icon: Building2 },
    { value: 'villa', label: 'Villa / House', icon: Home },
    { value: 'plot', label: 'Plot / Land', icon: Maximize },
    { value: 'penthouse', label: 'Penthouse', icon: Building2 },
  ],
  commercial: [
    { value: 'office', label: 'Office Space', icon: Building2 },
    { value: 'shop', label: 'Shop / Showroom', icon: DoorOpen },
    { value: 'warehouse', label: 'Warehouse', icon: Building2 },
    { value: 'co_working', label: 'Co-working', icon: Users },
  ],
  agricultural: [
    { value: 'farmland', label: 'Farm Land', icon: Trees },
    { value: 'farmhouse', label: 'Farm House', icon: Home },
    { value: 'agricultural_plot', label: 'Agricultural Plot', icon: Maximize },
  ]
};

// Process trail steps
const processSteps = [
  { step: 1, title: 'Search', description: 'Browse listings', icon: Search, active: true },
  { step: 2, title: 'Shortlist', description: 'Save favorites', icon: Star },
  { step: 3, title: 'Visit', description: 'Schedule tour', icon: MapPin },
  { step: 4, title: 'Finalize', description: 'Complete deal', icon: Shield },
];

const ProcessTrail = ({ currentStep = 0 }) => (
  <div className="relative mb-8" data-testid="search-process-trail">
    <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200">
      <div 
        className="h-full bg-gradient-to-r from-primary to-amber-500 transition-all duration-500"
        style={{ width: `${(currentStep / (processSteps.length - 1)) * 100}%` }}
      />
    </div>
    <div className="relative flex justify-between">
      {processSteps.map((item, idx) => (
        <div key={idx} className={`flex flex-col items-center text-center ${idx <= currentStep ? 'opacity-100' : 'opacity-40'}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all ${
            idx < currentStep ? 'bg-primary text-white' : idx === currentStep ? 'bg-amber-500 text-white ring-4 ring-amber-100' : 'bg-gray-100 text-gray-400'
          }`}>
            {idx < currentStep ? <CheckCircle className="w-5 h-5" /> : <item.icon className="w-4 h-4" />}
          </div>
          <span className="text-xs font-medium">{item.title}</span>
        </div>
      ))}
    </div>
  </div>
);

const PropertyCard = ({ property, wishlisted, onWishlist, onOpen, formatPrice }) => {
  // Engagement signal (display only)
  const [watchingCount] = useState(() => Math.floor(Math.random() * 15) + 3);
  const isHot = watchingCount > 10;

  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group"
      data-testid={`property-card-${property.property_id}`}
      onClick={() => onOpen(property)}
    >
      <div className="h-56 bg-gray-100 relative overflow-hidden">
        {property.images && property.images[0] ? (
          <img
            src={property.images[0]}
            alt={property.title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Building2 className="w-16 h-16 text-gray-300" />
          </div>
        )}
        <button
          className="absolute top-4 right-4 p-2 bg-white/90 backdrop-blur rounded-full hover:bg-white transition-colors z-10"
          onClick={(e) => {
            e.stopPropagation();
            onWishlist(property.property_id);
          }}
          data-testid={`wishlist-btn-${property.property_id}`}
        >
          <Heart className={`w-5 h-5 ${wishlisted ? 'fill-red-500 text-red-500' : 'text-gray-600 hover:text-red-500'}`} />
        </button>

        <div className={`absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
          isHot ? 'bg-red-500 text-white animate-pulse' : 'bg-white/90 backdrop-blur text-gray-700'
        }`}>
          <Users className="w-3.5 h-3.5" />
          {watchingCount} watching
          {isHot && <span className="ml-1">🔥</span>}
        </div>

        <div className="absolute bottom-4 left-4 flex items-center gap-2">
          <span className="bg-primary text-white text-xs font-medium px-3 py-1 rounded-full">
            {property.property_type}
          </span>
          {property.is_verified ? (
            <span className="bg-green-600 text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1" data-testid={`verified-badge-${property.property_id}`}>
              <BadgeCheck className="w-3 h-3" />Verified
            </span>
          ) : property.seeded ? (
            <span className="bg-amber-500/90 text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1" data-testid={`unverified-badge-${property.property_id}`}>
              <AlertTriangle className="w-3 h-3" />Unverified
            </span>
          ) : null}
        </div>
      </div>
      <div className="p-6">
        <h3 className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors">
          {property.title}
        </h3>
        <p className="text-sm text-gray-500 mb-4 flex items-center">
          <MapPin className="w-4 h-4 mr-1" />
          {property.city}, {property.state}
        </p>
        {property.seeded && !property.is_verified && (
          <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-lg mb-3 inline-block" data-testid={`seeded-label-${property.property_id}`}>
            Added by ZenStudios
          </p>
        )}
        <div className="flex flex-wrap gap-3 mb-4 text-sm text-gray-600">
          {property.bedrooms && (
            <span className="flex items-center gap-1 bg-gray-50 px-3 py-1 rounded-full">
              <Bed className="w-4 h-4" /> {property.bedrooms} BHK
            </span>
          )}
          {property.bathrooms && (
            <span className="flex items-center gap-1 bg-gray-50 px-3 py-1 rounded-full">
              <DoorOpen className="w-4 h-4" /> {property.bathrooms} Bath
            </span>
          )}
          <AreaBadge
            sqftValue={property.area_sqft}
            unit={property.area_input_unit || 'sqft'}
            className="bg-gray-50 rounded-full"
          />
        </div>
        <div className="flex justify-between items-center pt-4 border-t">
          <span className="text-2xl font-bold text-primary">
            {formatPrice(property.price)}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(property);
            }}
          >
            View Details <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const SEARCH_CACHE_KEY = 'zenstudios_buy_search';

const RealEstate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [fallback, setFallback] = useState([]);
  const [fallbackLevel, setFallbackLevel] = useState(null);
  const [searchedCity, setSearchedCity] = useState('');
  const [searchedState, setSearchedState] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [filters, setFilters] = useState({
    property_type: 'residential',
    property_subtype: '',
    city: searchParams.get('city') || '',
    bedrooms: '',
    bathrooms: '',
    min_price: '',
    max_price: '',
    min_area: '',
    max_area: '',
    area_unit: 'sqft',
    meeting_rooms: '',
    amenities: '',
    listing_type: 'sale'
  });
  const [wishlistedIds, setWishlistedIds] = useState(new Set());

  const currentSubtypes = propertySubtypes[filters.property_type] || [];
  const isResidential = filters.property_type === 'residential';
  const isCommercial = filters.property_type === 'commercial';
  const isAgricultural = filters.property_type === 'agricultural';

  // Fetch user's wishlist on mount
  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        const response = await apiClient.get('/properties/wishlist/my');
        if (response.data && Array.isArray(response.data)) {
          setWishlistedIds(new Set(response.data.map(p => p.property_id)));
        }
      } catch (error) {
        // User might not be logged in - use local storage fallback
        const localWishlist = localStorage.getItem('zenstudios_wishlist');
        if (localWishlist) {
          setWishlistedIds(new Set(JSON.parse(localWishlist)));
        }
      }
    };
    fetchWishlist();
  }, []);

  // Sort properties for display when sortBy changes (derived, no effect)
  const sortedProperties = React.useMemo(() => {
    const arr = [...properties];
    arr.sort((a, b) => {
      if (sortBy === 'price_low') return a.price - b.price;
      if (sortBy === 'price_high') return b.price - a.price;
      return 0;
    });
    return arr;
  }, [properties, sortBy]);

  const handleWishlist = async (propertyId) => {
    // Helper function to handle local wishlist
    const toggleLocalWishlist = (isRemove) => {
      if (isRemove) {
        setWishlistedIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(propertyId);
          localStorage.setItem('zenstudios_wishlist', JSON.stringify([...newSet]));
          return newSet;
        });
        toast.success('Removed from wishlist');
      } else {
        setWishlistedIds(prev => {
          const newSet = new Set([...prev, propertyId]);
          localStorage.setItem('zenstudios_wishlist', JSON.stringify([...newSet]));
          return newSet;
        });
        toast.success('Added to wishlist');
      }
    };

    const isRemoving = wishlistedIds.has(propertyId);
    
    try {
      if (isRemoving) {
        await apiClient.delete(`/properties/wishlist/${propertyId}`);
      } else {
        await apiClient.post(`/properties/wishlist/${propertyId}`);
      }
      toggleLocalWishlist(isRemoving);
    } catch (error) {
      // If not authenticated or any error, use local wishlist
      toggleLocalWishlist(isRemoving);
      if (error.response?.status !== 401 && error.response?.data?.detail !== 'Not authenticated') {
        console.error('Wishlist error:', error);
      }
    }
  };

  const persistSearch = (data, usedFilters) => {
    try {
      sessionStorage.setItem(SEARCH_CACHE_KEY, JSON.stringify({
        filters: usedFilters,
        properties: data.results || [],
        fallback: data.fallback || [],
        fallbackLevel: data.fallback_level || null,
        searchedCity: data.searched_city || usedFilters.city || '',
        searchedState: data.searched_state || '',
        sortBy,
      }));
    } catch {
      /* ignore storage errors */
    }
  };

  const fetchProperties = async (overrideFilters) => {
    const activeFilters = overrideFilters || filters;
    setLoading(true);
    try {
      const params = new URLSearchParams();

      // Process filters, converting area to sqft if needed
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value && value !== 'any') {
          if (key === 'min_area' && value) {
            params.append('min_area', toSqft(parseFloat(value), activeFilters.area_unit).toString());
          } else if (key === 'max_area' && value) {
            params.append('max_area', toSqft(parseFloat(value), activeFilters.area_unit).toString());
          } else if (key !== 'area_unit') {
            params.append(key, value);
          }
        }
      });

      const response = await apiClient.get(`/properties/search?${params.toString()}`);
      const data = response.data || {};
      setProperties(data.results || []);
      setFallback(data.fallback || []);
      setFallbackLevel(data.fallback_level || null);
      setSearchedCity(data.searched_city || activeFilters.city || '');
      setSearchedState(data.searched_state || '');
      persistSearch(data, activeFilters);
    } catch (error) {
      // No dummy data: surface a clean empty state instead.
      setProperties([]);
      setFallback([]);
      setFallbackLevel(null);
      setSearchedCity(activeFilters.city || '');
      setSearchedState('');
    } finally {
      setLoading(false);
    }
  };

  // On mount: restore last search (back-navigation) or run a fresh
  // location-aware search using hybrid IP detection / stored city.
  useEffect(() => {
    const init = async () => {
      try {
        const cached = sessionStorage.getItem(SEARCH_CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed.filters) setFilters((prev) => ({ ...prev, ...parsed.filters }));
          setProperties(parsed.properties || []);
          setFallback(parsed.fallback || []);
          setFallbackLevel(parsed.fallbackLevel || null);
          setSearchedCity(parsed.searchedCity || '');
          setSearchedState(parsed.searchedState || '');
          if (parsed.sortBy) setSortBy(parsed.sortBy);
          setLoading(false);
          return;
        }
      } catch {
        /* ignore parse errors */
      }

      const urlCity = searchParams.get('city') || '';
      const city = await resolveInitialCity(urlCity);
      const initFilters = { ...filters, city };
      setFilters(initFilters);
      await fetchProperties(initFilters);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    if (filters.city) setStoredCity(filters.city);
    fetchProperties();
  };

  const formatPrice = (price) => {
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(2)} Cr`;
    }
    return `₹${(price / 100000).toFixed(2)} L`;
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="realestate-page">
      {/* Hero Search Section */}
      <section className="relative py-16 bg-gradient-to-br from-primary/5 via-white to-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="realestate-title">
              Find Your Perfect Property
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Search from hundreds of verified listings across residential, commercial, and agricultural properties.
            </p>
          </div>

          {/* Process Trail */}
          <div className="max-w-2xl mx-auto">
            <ProcessTrail currentStep={0} />
          </div>

          {/* Main Search Bar */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 max-w-5xl mx-auto" data-testid="property-filters">
            {/* Property Type Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {Object.entries(propertySubtypes).map(([type, subtypes]) => (
                <button
                  key={type}
                  onClick={() => setFilters({...filters, property_type: type, property_subtype: '', bedrooms: '', area_unit: getDefaultUnit(type)})}
                  className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap ${
                    filters.property_type === type 
                      ? 'bg-primary text-white shadow-lg' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-type-${type}`}
                >
                  {type === 'residential' && <Home className="w-4 h-4 inline mr-2" />}
                  {type === 'commercial' && <Building2 className="w-4 h-4 inline mr-2" />}
                  {type === 'agricultural' && <Trees className="w-4 h-4 inline mr-2" />}
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            {/* Subtype Selection */}
            <div className="flex flex-wrap gap-2 mb-6">
              {currentSubtypes.map((subtype) => (
                <button
                  key={subtype.value}
                  onClick={() => setFilters({...filters, property_subtype: subtype.value, area_unit: getDefaultUnit(subtype.value)})}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    filters.property_subtype === subtype.value 
                      ? 'bg-primary/10 text-primary border-2 border-primary' 
                      : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:border-gray-200'
                  }`}
                >
                  <subtype.icon className="w-4 h-4" />
                  {subtype.label}
                </button>
              ))}
            </div>

            {/* Search Fields */}
            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Location</Label>
                <LocationSearch
                  placeholder="Search city, locality..."
                  value={filters.city}
                  onChange={(val) => setFilters({...filters, city: val})}
                  types="city,town,locality"
                  className="[&_input]:rounded-xl"
                  data-testid="filter-city"
                  onLocationSelect={(location) => {
                    if (location) {
                      setFilters({...filters, city: location.city || location.name});
                    }
                  }}
                />
              </div>

              {/* Dynamic fields based on property type */}
              {isResidential && filters.property_subtype !== 'plot' && (
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Bedrooms</Label>
                  <Select value={filters.bedrooms} onValueChange={(v) => setFilters({...filters, bedrooms: v})}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="1">1 BHK</SelectItem>
                      <SelectItem value="2">2 BHK</SelectItem>
                      <SelectItem value="3">3 BHK</SelectItem>
                      <SelectItem value="4">4 BHK</SelectItem>
                      <SelectItem value="5">5+ BHK</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {isCommercial && filters.property_subtype === 'office' && (
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Meeting Rooms</Label>
                  <Select value={filters.meeting_rooms} onValueChange={(v) => setFilters({...filters, meeting_rooms: v})}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="1">1-2 Rooms</SelectItem>
                      <SelectItem value="3">3-5 Rooms</SelectItem>
                      <SelectItem value="6">6+ Rooms</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {(isCommercial || filters.property_subtype === 'plot' || filters.property_type === 'agricultural') && (
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Area</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Min Area"
                      type="number"
                      className="rounded-xl flex-1"
                      value={filters.min_area}
                      onChange={(e) => setFilters({...filters, min_area: e.target.value})}
                      data-testid="filter-min-area"
                    />
                    <Select 
                      value={filters.area_unit} 
                      onValueChange={(v) => setFilters({...filters, area_unit: v})}
                    >
                      <SelectTrigger className="rounded-xl w-24" data-testid="filter-area-unit">
                        <SelectValue placeholder="Unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {AREA_UNIT_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Budget</Label>
                <Select value={filters.max_price} onValueChange={(v) => setFilters({...filters, max_price: v === 'any' ? '' : v})}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Max Budget" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="5000000">Up to ₹50 Lakhs</SelectItem>
                    <SelectItem value="10000000">Up to ₹1 Crore</SelectItem>
                    <SelectItem value="20000000">Up to ₹2 Crore</SelectItem>
                    <SelectItem value="50000000">Up to ₹5 Crore</SelectItem>
                    <SelectItem value="100000000">₹5 Crore+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={handleSearch} 
                className="rounded-xl bg-primary h-auto py-3 md:col-span-1"
                data-testid="search-properties-btn"
              >
                <Search className="w-5 h-5 mr-2" /> Search
              </Button>
            </div>

            {/* Advanced Filters Toggle */}
            <button 
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-primary font-medium flex items-center gap-1 hover:underline"
            >
              <Filter className="w-4 h-4" />
              {showAdvanced ? 'Hide' : 'Show'} Advanced Filters
            </button>

            {/* Advanced Filters */}
            {showAdvanced && (
              <div className="mt-4 pt-4 border-t grid md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Min Price</Label>
                  <Input
                    placeholder="₹ Min"
                    type="number"
                    className="rounded-xl"
                    value={filters.min_price}
                    onChange={(e) => setFilters({...filters, min_price: e.target.value})}
                    data-testid="filter-min-price"
                  />
                </div>
                {isResidential && (
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Bathrooms</Label>
                    <Select value={filters.bathrooms} onValueChange={(v) => setFilters({...filters, bathrooms: v})}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="1">1</SelectItem>
                        <SelectItem value="2">2</SelectItem>
                        <SelectItem value="3">3+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Max Area</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Max"
                      type="number"
                      className="rounded-xl flex-1"
                      value={filters.max_area}
                      onChange={(e) => setFilters({...filters, max_area: e.target.value})}
                      data-testid="filter-max-area"
                    />
                    <Select 
                      value={filters.area_unit} 
                      onValueChange={(v) => setFilters({...filters, area_unit: v})}
                    >
                      <SelectTrigger className="rounded-xl w-24" data-testid="filter-area-unit-advanced">
                        <SelectValue placeholder="Unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {AREA_UNIT_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Min Area for residential properties */}
                {isResidential && filters.property_subtype !== 'plot' && (
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Min Area</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Min"
                        type="number"
                        className="rounded-xl flex-1"
                        value={filters.min_area}
                        onChange={(e) => setFilters({...filters, min_area: e.target.value})}
                        data-testid="filter-min-area-residential"
                      />
                      <Select 
                        value={filters.area_unit} 
                        onValueChange={(v) => setFilters({...filters, area_unit: v})}
                      >
                        <SelectTrigger className="rounded-xl w-24">
                          <SelectValue placeholder="Unit" />
                        </SelectTrigger>
                        <SelectContent>
                          {AREA_UNIT_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-2xl font-bold">
                {filters.property_type ? `${filters.property_type.charAt(0).toUpperCase() + filters.property_type.slice(1)} Properties` : 'All Properties'}
              </h2>
              <p className="text-gray-500">{properties.length} properties found</p>
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40 rounded-xl" data-testid="sort-dropdown">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Properties Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="text-gray-500 mt-4">Searching properties...</p>
            </div>
          ) : properties.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="properties-grid">
              {sortedProperties.map((property) => (
                <PropertyCard
                  key={property.property_id}
                  property={property}
                  wishlisted={wishlistedIds.has(property.property_id)}
                  onWishlist={handleWishlist}
                  onOpen={(p) => navigate(propertyPath(p))}
                  formatPrice={formatPrice}
                />
              ))}
            </div>
          ) : (
            <div data-testid="no-properties-state">
              {/* No exact matches — friendly verbiage + requirement CTA */}
              <div className="text-center py-12 bg-gradient-to-br from-amber-50 to-white border border-amber-100 rounded-2xl px-6" data-testid="no-properties-msg">
                <Building2 className="w-14 h-14 text-amber-400 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold mb-3">
                  {searchedCity
                    ? `No live listings in ${searchedCity} just yet`
                    : 'No matching listings just yet'}
                </h3>
                <p className="text-gray-600 max-w-xl mx-auto mb-6">
                  We&apos;re constantly adding new properties{searchedCity ? ` in ${searchedCity}` : ''}.
                  Please check back again soon — or tell us exactly what you&apos;re looking for and
                  our team will get back to you with relevant options as they become available.
                </p>
                <div className="flex gap-3 justify-center flex-wrap">
                  <Button
                    className="rounded-full bg-primary px-8"
                    onClick={() => navigate('/buy/post-requirement')}
                    data-testid="post-requirement-btn"
                  >
                    Submit Your Requirement <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-full px-8"
                    onClick={() => fetchProperties()}
                    data-testid="check-again-btn"
                  >
                    Check Again
                  </Button>
                </div>
              </div>

              {/* Recently-added fallback, widening scope city -> state -> country */}
              {fallback.length > 0 && (
                <div className="mt-12" data-testid="fallback-section">
                  <h3 className="text-xl font-semibold mb-1" data-testid="fallback-heading">
                    {fallbackLevel === 'city' && `Recently added in ${searchedCity || 'your city'}`}
                    {fallbackLevel === 'state' && `Recently added across ${searchedState || 'the state'}`}
                    {fallbackLevel === 'country' && 'Recently added across India'}
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    While we find an exact match, here are the latest listings you might like.
                  </p>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="fallback-grid">
                    {fallback.map((property) => (
                      <PropertyCard
                        key={property.property_id}
                        property={property}
                        wishlisted={wishlistedIds.has(property.property_id)}
                        onWishlist={handleWishlist}
                        onOpen={(p) => navigate(propertyPath(p))}
                        formatPrice={formatPrice}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Can&apos;t Find What You&apos;re Looking For?</h2>
          <p className="text-gray-600 mb-8">
            Let us know your requirements and our property experts will find the perfect match for you.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              className="rounded-full bg-primary px-8"
              onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%20need%20personalized%20property%20recommendations.', '_blank')}
              data-testid="get-recommendations-btn"
            >
              Get Personalized Recommendations
            </Button>
            <Button 
              variant="outline" 
              className="rounded-full px-8"
              onClick={() => navigate('/sell/individual')}
              data-testid="list-property-cta-btn"
            >
              List Your Property for Sale
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RealEstate;
